import { Popover, Tooltip } from 'antd';
import Barchart from 'components/molecules/Barchart';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import Loader from 'components/molecules/Loader';
import { salesTileItemType } from 'components/pages/CommercialForecast';
import { growthAnalysisTableId, height, yAxisLabelClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { colorConstants } from 'config/colorConstants';
import _ from 'lodash';
import React, { forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import {
    calculateFontSize,
    fetchCurrency,
    fetchUnit,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    formatUnit,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { graphXAxisLabelFontSize } from '../GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import { salesDataType } from 'components/pages/CommercialForecast';
import { yAxisGraphWidth } from 'components/template/CommercialForecast/CFExpandedView';
import { salesGraphWidth } from '../GrowthAnalysis/SalesChartContainer/SalesChartContainer';

const expandMoreSvg = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M7.99967 14.6666C11.6816 14.6666 14.6663 11.6819 14.6663 7.99998C14.6663 4.31808 11.6816 1.33331 7.99967 1.33331C4.31778 1.33331 1.33301 4.31808 1.33301 7.99998C1.33301 11.6819 4.31778 14.6666 7.99967 14.6666Z"
            stroke="#44338A"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
        <path
            d="M9.81441 5.51062C10.1401 5.40206 10.303 5.34777 10.4113 5.38639C10.5055 5.42 10.5797 5.49416 10.6133 5.5884C10.6519 5.69669 10.5976 5.85954 10.489 6.18524L9.49733 9.16033C9.46641 9.25309 9.45096 9.29947 9.42461 9.33799C9.40127 9.3721 9.3718 9.40158 9.33768 9.42491C9.29917 9.45126 9.25278 9.46672 9.16003 9.49764L6.18494 10.4893C5.85924 10.5979 5.69639 10.6522 5.58809 10.6136C5.49385 10.58 5.41969 10.5058 5.38609 10.4116C5.34747 10.3033 5.40175 10.1404 5.51032 9.81472L6.50201 6.83963C6.53293 6.74687 6.54839 6.70049 6.57474 6.66197C6.59808 6.62786 6.62755 6.59838 6.66167 6.57505C6.70018 6.5487 6.74656 6.53324 6.83932 6.50232L9.81441 5.51062Z"
            stroke="#44338A"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
    </svg>
);

interface Props {
    exploreMoreOnClick: (item: any) => void;
    drillDownOnClick: (item: any) => void;
    index: number;
    expandedItem: any;
    drilledDownItem: any;
    disableItemHover: boolean;
    disableDrillDownHover: boolean;
    dropdownItems: any[];
    dropdownClickHandler: (item: any, attrType: any, index: number) => void;
    attrTypeOptions: any;
    data: salesDataType;
    attributeTypeItem: any;
    infiniteScrollerNext: (index: number, width: number) => void;
    infiniteScrollSettings: any;
    isTableExpanded: boolean;
    disableRightClick: boolean;
    salesTile: salesTileItemType;
    perGraphComponentWidth: number;
}

export const yAxisLabelMapping: any = {
    vol: '',
    val: 'Sales',
    units: 'Units',
    profit: 'Profit',
    frontline: 'Frontline Sales',
    net: 'Net Sales',
};

export const barWidth = calculateFontSize(window.innerWidth, 31);

const ForecastChartContainer = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const {
        disableDrillDownHover,
        exploreMoreOnClick,
        drillDownOnClick,
        index,
        expandedItem,
        disableItemHover,
        drilledDownItem,
        dropdownItems,
        dropdownClickHandler,
        attrTypeOptions,
        data,
        attributeTypeItem,
        infiniteScrollerNext,
        infiniteScrollSettings,
        isTableExpanded,
        disableRightClick,
        salesTile,
        perGraphComponentWidth,
    } = props;
    // const SalesChartContainer = forwardRef<HTMLDivElement, Props>((props, ref) => {
    const [isItemsModalOpen, setIsItemsModalOpen] = useState<boolean | number>(false);

    const { appConfig } = useContext(UserContext);

    const { chartData: salesData, graphLoading: loading, graphError: error, count } = data;
    const { width = [0, 0] } = infiniteScrollSettings;

    const handleRightClick = (e: any, index: number) => {
        e.preventDefault();
        setIsItemsModalOpen(index);
    };
    const hide = () => {
        setIsItemsModalOpen(false);
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) setIsItemsModalOpen(newOpen);
    };

    const infiniteScrollOnNext = (width: number, loading: boolean) => {
        if (loading) return;
        //  console.log('logging asd', width, loading);
        infiniteScrollerNext(index, width);
    };

    //listening to scroll
    useEffect(() => {
        // Table element
        const tableId = `${growthAnalysisTableId}-${index}`;
        const tableScrollableElement = document.getElementById(tableId)?.getElementsByClassName('ant-table-body')[0];
        // Graph element
        const graphElem = document.getElementById(`commercial-forecast-sales-infinite-scroll${index}`);

        // Function to synchronize scrolling of graph and table
        function scrollFunction(event: any) {
            if (event.target === graphElem) {
                // If graph is scrolled, scroll the table
                tableScrollableElement?.scrollTo?.({
                    left: Number(graphElem?.scrollLeft) * 1, // Giving a buffer because widths of table and chart are different
                    behavior: 'auto',
                });
            } else {
                // If table is scrolled, scroll the graph
                graphElem?.scrollTo?.({
                    left: Number(tableScrollableElement?.scrollLeft) * 1,
                    behavior: 'auto',
                });
            }
        }

        if (salesData?.data?.length > 0) {
            // Add event listeners for both graph and table
            graphElem?.addEventListener?.('scroll', scrollFunction, { passive: true });
            tableScrollableElement?.addEventListener?.('scroll', scrollFunction, { passive: true });

            return () => {
                // Cleanup: remove event listeners
                graphElem?.removeEventListener?.('scroll', scrollFunction);
                tableScrollableElement?.removeEventListener?.('scroll', scrollFunction);
            };
        }
    }, [salesData, loading, isTableExpanded]);

    const groupedChartColors = useMemo(() => {
        let colors: any[] = [];
        if (salesData?.legends?.length > 0) {
            colors = salesData?.legends?.map((item: any) => item.color);
        }
        return colors;
    }, [salesData]);

    // IF ERROR RETURN ERROR MESSAGE
    if (error && !loading && !salesData) return <ErrorBox msg={String(error)} />;

    // IF LOADING RETURN LOADER
    if (loading)
        return (
            <div style={{ height, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );
    const currency = fetchCurrency(appConfig);
    let YAxisResult: any = `Units`;

    // ELSE RETURN COMPONENT
    return (
        <section ref={ref} style={{ minHeight: height }}>
            {/* CATEGORY SALES CHART COMPONENT */}

            {salesData && (
                <div className="mb-4 flex justify-between items-center">
                    <p className={`${yAxisLabelClass} w-[120px]`}>{YAxisResult}</p>
                    <Legend data={salesData?.legends || []} />
                    <p className="w-[120px] font-normal text-greyFont font-xxs"></p>
                </div>
            )}

            <div id="commercialForecastSalesChart" className="flex ml-1 w-full">
                <div style={{ minWidth: yAxisGraphWidth }}>
                    <Barchart
                        colorKey="color"
                        dataset={[]}
                        height={height}
                        loading={false}
                        chartContainer={`SalesY-${props.index}`}
                        fields={['category', 'sales']}
                        minBound={scaleMinValue(105, salesData?.minVal)}
                        maxBound={scaleMaxValue(80, salesData?.maxVal)}
                        width={yAxisGraphWidth}
                        YGrid={false}
                        padding={[0, 0, 0, 0]}
                        yAxisLabelFormatter={(val: any) => {
                            return formatNumWithKMB(val);
                        }}
                        xAxisConfig={false}
                    />
                </div>
                <HorizontalInfiniteScroller
                    id={`commercial-forecast-sales-infinite-scroll${index}`}
                    hasMore={salesData?.data?.length - 1 < count || false} //subtracting 1 to remove total value which is the first item
                    scrollThreshold={0.9}
                    toScrollWidth={width[index]}
                    onNext={(width: number) => {
                        infiniteScrollOnNext(width, loading);
                    }}
                >
                    <div
                        id={`commercial-forecast-sales-infinite-scroll${index}`}
                        className="flex pb-[45px]"
                        style={{ maxWidth: '100%', overflowX: 'auto' }}
                    >
                        {salesData &&
                            'data' in salesData &&
                            Array.isArray(salesData.data) &&
                            salesData.data.map((data: any, idx: number) => {
                                const item = data;
                                const disabled = item[0]?.disableHover || disableItemHover || disableDrillDownHover;
                                const disabledBarExploreMore = item[0]?.disableHover || disableItemHover;

                                const isItemSelected =
                                    (expandedItem?.attributeType ===
                                        _.snakeCase(attributeTypeItem.selectedAttributeType) &&
                                        expandedItem?.attributeExId === item[0]?.category) ||
                                    (drilledDownItem?.attributeType ===
                                        _.snakeCase(attributeTypeItem.selectedAttributeType) &&
                                        drilledDownItem?.attributeExId === item[0]?.category);
                                //if idx===0 it means showing total, make total bar sticky so that it doesnt move with scroll
                                return (
                                    <div
                                        key={idx}
                                        style={{
                                            position: idx === 0 ? 'sticky' : 'static',
                                            left: idx === 0 ? 0 : 'auto',
                                            background: idx === 0 ? 'white' : 'transparent',
                                            zIndex: idx === 0 ? 10 : 'auto',
                                        }}
                                    >
                                        <Popover
                                            overlayStyle={{ padding: 0 }}
                                            trigger="click"
                                            open={idx === isItemsModalOpen}
                                            onOpenChange={handleOpenChange}
                                            placement={'right'}
                                            showArrow={false}
                                            overlayInnerStyle={{ position: 'relative', right: 20, padding: 0 }}
                                            content={
                                                <DropdownItems
                                                    item={item}
                                                    attrTypeOptions={attrTypeOptions}
                                                    items={dropdownItems}
                                                    onClick={dropdownClickHandler}
                                                />
                                            }
                                        >
                                            <div
                                                onClick={() => {
                                                    if (isItemsModalOpen !== false || disabled) return;
                                                    drillDownOnClick(item);
                                                }}
                                                style={{
                                                    position: 'relative',
                                                }}
                                                className={`${
                                                    !disabled || !disabledBarExploreMore
                                                        ? isItemSelected
                                                            ? 'bg-bgBackground'
                                                            : 'hover:bg-greyBackgroundAlt'
                                                        : ''
                                                } group`}
                                                onContextMenu={(e) => {
                                                    if (disabled || disableRightClick) return;
                                                    handleRightClick(e, idx);
                                                }}
                                            >
                                                {!disabledBarExploreMore && (
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            top: 5,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            zIndex: 9,
                                                            paddingBottom: 80,
                                                            height: 20,
                                                            width: '100%',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: 5,
                                                                left: '50%',
                                                                transform: 'translateX(-50%)',
                                                            }}
                                                            className={`${
                                                                !disabledBarExploreMore
                                                                    ? 'hidden group-hover:block cursor-pointer z-[9]'
                                                                    : ''
                                                            } `}
                                                            onClick={(e) => {
                                                                exploreMoreOnClick(item);
                                                                e.stopPropagation();
                                                            }}
                                                        >
                                                            <Tooltip
                                                                placement="top"
                                                                color="white"
                                                                destroyTooltipOnHide
                                                                overlayStyle={{ borderRadius: 4 }}
                                                                overlayInnerStyle={{
                                                                    fontSize: graphXAxisLabelFontSize,
                                                                    color: colorConstants.darkGrey,
                                                                }}
                                                                title={'Explore More'}
                                                            >
                                                                {expandMoreSvg}
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                )}
                                                <div style={{ height: height }}>
                                                    <Barchart
                                                        barWidth={barWidth}
                                                        //yAxisConfig={false}
                                                        dataset={item}
                                                        height={height}
                                                        loading={false}
                                                        chartContainer={`SalesX-${index}-${idx}`}
                                                        fields={['category', 'sales', 'type']}
                                                        intervalLabelTooltipKey={'labelSales'}
                                                        minBound={scaleMinValue(105, salesData?.minVal)}
                                                        maxBound={scaleMaxValue(80, salesData?.maxVal)}
                                                        width={perGraphComponentWidth || salesGraphWidth}
                                                        tooltipFormatter={(xAxis, val) => {
                                                            return {
                                                                name: xAxis,
                                                                value: formatNumWithKMBAlt(val, 2, 2),
                                                            };
                                                        }}
                                                        intervalFormatter={(val) => {
                                                            return formatNumWithKMBAlt(val, 2, 2);
                                                        }}
                                                        enableGroupedChart={item?.length > 1}
                                                        groupedChartColor={groupedChartColors}
                                                        //addPercentageInIntervalLabel={absTile.key === 'percentage'}
                                                        colorKey={'color'}
                                                        disableIntervalLabel={item?.length > 1}
                                                    />
                                                </div>
                                            </div>
                                        </Popover>
                                        <div
                                            style={{
                                                width: perGraphComponentWidth || salesGraphWidth,
                                                overflowWrap: 'anywhere',
                                                // wordBreak: 'break-word',
                                                fontSize: graphXAxisLabelFontSize,
                                            }}
                                            className="text-center p-[2px]"
                                        >
                                            {item[0]?.category}
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                </HorizontalInfiniteScroller>
            </div>
        </section>
    );
});

export default ForecastChartContainer;

type DropdownItemsType = {
    items: any[];
    item: any;
    onClick: (item: any, attrType: any, index: number) => void;
    attrTypeOptions: any;
};

export const DropdownItems = ({ item, items, onClick, attrTypeOptions }: DropdownItemsType) => {
    return (
        <div className="max-h-[200px] pr-1 overflow-auto">
            {items?.length > 0 ? (
                items.map((attrType: any) => {
                    return (
                        <div
                            onClick={() => {
                                onClick(item, attrType, attrTypeOptions.data?.length - items.length);
                            }}
                            className="p-1 text-purpleFont cursor-pointer hover:bg-buttonBgColor"
                            key={attrType?.value}
                        >
                            {attrType.label}
                        </div>
                    );
                })
            ) : (
                <div>No data</div>
            )}
        </div>
    );
};
